/**
 * System of a Down — Toxicity (2001)
 *
 * Source of truth: `nyx_landing_soad.json`.
 * Analytical prose is carried over verbatim wherever the local schema allows.
 * Schema-fit transformations:
 *
 *   - hero / deepDiveLine / sectionTitle: decomposed from
 *     `album_form_and_behavior.form_behavior_gap`.
 *   - tensions[].anchors: split from the source anchor sentences into the
 *     existing `{ track, note }` shape.
 *   - signature_moments[].pull / .why: split from the single
 *     `why_it_matters` paragraph at the first sentence boundary.
 *   - `data_integrity_notes`, `structural_signals`, `break_pattern_tracks`,
 *     `emotional_clusters`, and `lyrical_architecture.note` are not shipped
 *     because the landing schema has no rendered slots for them.
 */

import type { AlbumLibraryEntry, DeepDive } from '@/types/album';

export const library = {
  id: 'toxicity',
  artist: 'System of a Down',
  album: 'Toxicity',
  year: 2001,
  genre: 'Alt-metal / nu-metal',
  hook:
    'Es un avance comercial, sí, pero sobre todo es una refinación feroz: conserva la incomodidad del debut y la empaqueta en estructuras que se te quedan clavadas.',
  palette: { bg: '#11110F', fg: '#F0E7D2', accent: '#C54F32' },
  typeStyle: 'stencil',
  available: true,
  hero: {
    apparent: 'el disco que convirtió el caos de System of a Down en canciones enormes',
    key: 'una sobrecarga de sistema',
    coda: 'donde protesta, chiste corporal y oración terminan hablando el mismo idioma.',
  },
  deepDiveLine: {
    apparent: 'el disco que convirtió el caos de System of a Down en canciones enormes',
    key: 'una sobrecarga de sistema donde protesta, chiste corporal y oración terminan hablando el mismo idioma',
  },
  sectionTitle: {
    pre: 'Toxicity, leído como una',
    em: 'sobrecarga de sistema',
    post: 'donde protesta, chiste corporal y oración terminan hablando el mismo idioma.',
  },
  songsTitle: 'Tres canciones, leídas con cuidado.',
  footerCredit: 'Análisis original · Toxicity · System of a Down · 2001',
  coverImage: '/images/toxicity.jpg',
} as const satisfies AlbumLibraryEntry;

export const deepDive: DeepDive = {
  artist: 'System of a Down',
  title: 'Toxicity',
  year: '2001',
  position_label: 'explosión refinada',
  apparent_form:
    'Sobre el papel, Toxicity parece un disco de alt-metal y nu-metal de gran alcance, lleno de canciones cortas, sencillos enormes, política frontal y rareza de banda imposible de domesticar.',
  actual_behavior:
    'Al escucharlo seguido, el disco funciona por sacudidas muy controladas: estalla, corta, se vuelve ridículo, se pone devocional y vuelve a morder antes de que puedas ordenar la reacción. La secuencia convierte cambios de tempo, voces de personaje, armonías casi litúrgicas y riffs secos en una misma respuesta física al encierro, a la vigilancia y al asco social.',
  form_behavior_gap:
    'Parece el disco que convirtió el caos de System of a Down en canciones enormes; se mueve como una sobrecarga de sistema donde protesta, chiste corporal y oración terminan hablando el mismo idioma.',
  previous_context:
    'System of a Down ya tenía la mezcla de metal, hardcore, rareza armenio-estadounidense y sátira política, pero sonaba más crudo y menos concentrado en forma de canción.',
  next_context:
    'Steal This Album! llegó al año siguiente con material vinculado a las sesiones y filtraciones del periodo Toxicity, más disperso en superficie pero muy cercano en ADN.',
  position_summary:
    'Toxicity es el segundo álbum de estudio y el punto donde la banda deja de ser una anomalía brillante de la escena pesada angelina para convertirse en un lenguaje propio a escala masiva. Es un avance comercial, sí, pero sobre todo es una refinación feroz: conserva la incomodidad del debut y la empaqueta en estructuras que se te quedan clavadas.',

  arch_type: 'hinge-driven',
  arch_summary:
    'Toxicity avanza por bisagras más que por una curva suave: bloques de velocidad, cortes absurdos, aperturas melódicas y cierres rituales se alternan para mantener al oyente en alerta. El disco no sube simplemente de intensidad; cambia de forma cada vez que una emoción amenaza con estabilizarse.',
  arch_opening:
    "Los cinco primeros temas entran como una redada. 'Prison Song', 'Needles' y 'Deer Dance' fijan un registro de denuncia, cuerpo invadido y violencia pública, mientras 'Jet Pilot' y 'X' reducen la duración hasta que el disco parece funcionar por espasmos.",
  arch_middle:
    "El centro real está entre 'Chop Suey!', 'Forest', 'ATWA' y 'Science', donde el disco deja de ser solo una cadena de golpes y empieza a mostrar su lado elegíaco. 'Bounce' aparece justo después de 'Chop Suey!' como un chiste agresivo colocado donde otro álbum pondría solemnidad, y eso mantiene viva la inestabilidad. 'Forest' y 'ATWA' abren espacio melódico sin quitar presión; son descansos con los dientes apretados.",
  arch_closing:
    "El tramo final junta la canción titular, la fiebre de 'Psycho' y la amplitud de 'Aerials / Arto'. 'Toxicity' funciona como resumen sonoro del disco, 'Psycho' devuelve la compulsión al cuerpo y el cierre abre una salida vertical, casi ceremonial, que no resuelve la rabia pero la deja flotando en otro registro.",
  arch_sequencing_note:
    "La colocación de 'Chop Suey!' en el sexto puesto es el primer gran eje: llega después de la sección más comprimida y convierte el impacto inicial en lenguaje espiritual y emocional.",

  tensions: [
    {
      label: 'Denunciar el sistema vs convertir la denuncia en carnaval nervioso',
      description:
        'Toxicity está lleno de rabia política, pero rara vez la entrega en forma solemne. \'Prison Song\' arranca como manifiesto contra el encarcelamiento masivo y enseguida lo vuelve mecanismo, con la banda entrando y saliendo como una alarma defectuosa. \'Deer Dance\' y \'Bounce\' llevan esa lógica más lejos: la violencia pública y el absurdo de patio de colegio comparten el mismo nervio rítmico, como si el disco desconfiara de cualquier forma de denuncia que se quede quieta.',
      anchors: [
        {
          track: 'Prison Song',
          note: "abre con el golpe directo de 'They're trying to build a prison.'",
        },
        {
          track: 'Deer Dance',
          note: 'convierte una escena de represión policial en un pulso casi bailable.',
        },
        {
          track: 'Bounce',
          note: "usa el grito de 'pogo' como si la canción fuera una caricatura de sí misma.",
        },
      ],
    },
    {
      label: 'Buscar trascendencia vs quedarse atrapado en el cuerpo',
      description:
        "El álbum está obsesionado con la elevación, pero cada intento de subir trae una caída corporal o social. 'Chop Suey!' hace que una canción de empujones se abra de golpe hacia una plegaria, y esa transición cambia la temperatura de todo el disco. 'Science' y 'Aerials / Arto' empujan la misma pregunta desde lados opuestos: la mente quiere explicación, la voz quiere trascendencia, y el cuerpo sigue atrapado en la máquina.",
      anchors: [
        {
          track: 'Chop Suey!',
          note: "pasa de los cortes secos al ruego de 'Father, into your hands.'",
        },
        {
          track: 'Science',
          note: 'enfrenta fe, razón y pérdida de sentido con un riff que no deja descansar.',
        },
        {
          track: 'Aerials / Arto',
          note: 'termina llevando el disco hacia una forma de canto ritual.',
        },
      ],
    },
    {
      label: 'Caos teatral vs control de banda quirúrgico',
      description:
        "La gracia del disco es que suena fuera de control justo porque la banda está tocando con una disciplina brutal. Los cortes de 'Jet Pilot' y 'X' no se sienten como bocetos; son ataques completos, sin grasa. En 'Toxicity' y 'Psycho', John Dolmayan y Shavo Odadjian sostienen el suelo mientras Serj Tankian y Daron Malakian cambian de máscara, de registro y de volumen con una rapidez que debería romper la canción. No la rompe. La vuelve más peligrosa.",
      anchors: [
        {
          track: 'Jet Pilot y X',
          note: 'comprimen ideas enteras en ráfagas de menos de dos minutos.',
        },
        {
          track: 'Toxicity',
          note: 'alterna arpegios limpios, estallidos de batería y coros expansivos sin perder el pulso.',
        },
        {
          track: 'Psycho',
          note: 'parece descontrolarse, pero la banda entra y sale con precisión casi matemática.',
        },
      ],
    },
  ],

  signature_moments: [
    {
      track: 'Chop Suey!',
      anchor: "El giro hacia 'Father, into your hands' después de los cortes secos.",
      pull:
        'Ese momento revela que la teatralidad del disco no es solo mueca ni rareza vocal.',
      why: 'La canción venía funcionando como una serie de empujones, y de pronto se abre en forma de plegaria trágica; después de escucharlo así, los cambios bruscos de todo Toxicity empiezan a sonar como entradas y salidas de estados mentales, no como trucos de arreglo.',
    },
    {
      track: 'Aerials / Arto',
      anchor: 'La transición desde el cierre de Aerials hacia el canto de Arto.',
      pull:
        'El álbum termina saliendo por una puerta que no pertenece del todo al metal estadounidense de 2001.',
      why: 'Ese cierre reubica la rareza de la banda: los giros melódicos, las voces tensas y la sensación de ceremonia venían anunciando una memoria cultural más profunda que el simple impacto de guitarra.',
    },
  ],

  motifs: [
    'El encierro institucional aparece como cárcel, escuela, ciencia convertida en autoridad y control de masas en Prison Song, Deer Dance, Shimmy y Science.',
    'El cuerpo aparece como lugar de invasión, compulsión o burla física en Needles, Bounce, Psycho y Chop Suey!.',
    'La elevación espiritual aparece ligada a caída, sacrificio o salida del yo en Chop Suey!, Science, Toxicity y Aerials / Arto.',
  ],

  enduring:
    'Toxicity ha durado porque nunca pide permiso para separar lo serio de lo absurdo. La banda entendió que una canción podía hablar de prisiones, adicción, guerra cultural, religión y alienación, y aun así tener un estribillo que una multitud pudiera gritar como si fuera una broma compartida. Esa mezcla sigue funcionando porque el mundo que describe tampoco aprendió a compartimentarse: la propaganda, el entretenimiento, la violencia y la espiritualidad aparecen en la misma pantalla. El disco no envejece como documento de 2001 solamente. Envejece como una máquina de alarma que todavía sabe exactamente dónde poner el dedo.',

  tracks: [
    { n: 1, title: 'Prison Song', ms: 201493, hasMeaning: true },
    { n: 2, title: 'Needles', ms: 193773 },
    { n: 3, title: 'Deer Dance', ms: 175066, hasMeaning: true },
    { n: 4, title: 'Jet Pilot', ms: 126466 },
    { n: 5, title: 'X', ms: 118560 },
    { n: 6, title: 'Chop Suey!', ms: 210240, hinge: true },
    { n: 7, title: 'Bounce', ms: 114226 },
    { n: 8, title: 'Forest', ms: 240106 },
    { n: 9, title: 'ATWA', ms: 176200, hasMeaning: true },
    { n: 10, title: 'Science', ms: 163066 },
    { n: 11, title: 'Shimmy', ms: 111066 },
    { n: 12, title: 'Toxicity', ms: 218933 },
    { n: 13, title: 'Psycho', ms: 225893 },
    { n: 14, title: 'Aerials / Arto', ms: 371040 },
  ],

  songs_with_meaning: ['Prison Song', 'Deer Dance', 'ATWA'],
};
